/**
 * Firebase Cloud Messaging for customers, caregivers, care managers, and ops.
 */

import api from "./api";

const PLATFORM_PUSH_EVENT = "nema:platform-push";
/** @deprecated use PLATFORM_PUSH_EVENT */
const BOOKING_PUSH_EVENT = "nema:booking-push";
const FCM_TOKEN_STORAGE_KEY = "nema_fcm_token";
let registrationStarted = false;
let registrationInFlight = null;
let lastFcmToken = null;
let lastRegistrationReason = null;
let serverDeliveryWarned = false;
let foregroundMessageListenerBound = false;

function readStoredFcmToken() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(FCM_TOKEN_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function persistFcmToken(token) {
  lastFcmToken = token || null;
  if (typeof window === "undefined") return;
  try {
    if (token) window.localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
}

if (typeof window !== "undefined") {
  const stored = readStoredFcmToken();
  if (stored) lastFcmToken = stored;
}

export function resetPushRegistration() {
  registrationStarted = false;
  lastRegistrationReason = null;
  foregroundMessageListenerBound = false;
  persistFcmToken(null);
}

/** Clear stale service workers / IndexedDB that cause Chrome error 20 (push service error). */
export async function clearMessagingClientState() {
  resetPushRegistration();
  if (typeof navigator === "undefined" || !navigator.serviceWorker) return;

  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((reg) => reg.unregister()));
  } catch (err) {
    console.warn("[push] service worker unregister failed:", err?.message || err);
  }

  try {
    if (typeof indexedDB !== "undefined" && indexedDB.databases) {
      const dbs = await indexedDB.databases();
      await Promise.all(
        (dbs || [])
          .filter((db) => db.name && /firebase|fcm|nema-push/i.test(db.name))
          .map(
            (db) =>
              new Promise((resolve) => {
                const req = indexedDB.deleteDatabase(db.name);
                req.onsuccess = () => resolve();
                req.onerror = () => resolve();
                req.onblocked = () => resolve();
              })
          )
      );
    }
  } catch (err) {
    console.warn("[push] indexedDB cleanup failed:", err?.message || err);
  }
}

function isPushServiceError(err) {
  const code = String(err?.code ?? "");
  const msg = String(err?.message ?? "").toLowerCase();
  return code === "20" || msg.includes("push service error") || msg.includes("aborterror");
}

export function getLastPushRegistrationReason() {
  return lastRegistrationReason;
}

export function isPushTokenRegisteredLocally() {
  return Boolean(getLastFcmToken());
}

export function getLastFcmToken() {
  return lastFcmToken || readStoredFcmToken();
}

function isEdgeBrowser() {
  if (typeof navigator === "undefined") return false;
  return /Edg\//.test(navigator.userAgent);
}

function isChromeBrowser() {
  if (typeof navigator === "undefined") return false;
  return /Chrome\//.test(navigator.userAgent) && !/Edg\//.test(navigator.userAgent);
}

export function isChromePushBrowser() {
  return isChromeBrowser();
}

export function detectWebPushPlatform() {
  if (isEdgeBrowser()) return "web-edge";
  if (isChromeBrowser()) return "web-chrome";
  if (typeof navigator !== "undefined" && /Firefox\//.test(navigator.userAgent)) return "web-firefox";
  return "web";
}

export function getLocalPushDiagnostics() {
  return {
    permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
    localToken: Boolean(lastFcmToken),
    lastReason: lastRegistrationReason,
    platform: detectWebPushPlatform(),
    secureContext: isSecurePushContext(),
  };
}

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => {
        reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`));
      }, ms);
    }),
  ]);
}
async function waitForServiceWorkerReady(registration, timeoutMs = 15000) {
  if (registration.active) return registration;

  const readyPromise = (async () => {
    const worker = registration.installing || registration.waiting;
    if (!worker) {
      await navigator.serviceWorker.ready;
      return registration;
    }
    await new Promise((resolve) => {
      if (worker.state === "activated") {
        resolve();
        return;
      }
      const onChange = () => {
        if (worker.state === "activated") {
          worker.removeEventListener("statechange", onChange);
          resolve();
        }
      };
      worker.addEventListener("statechange", onChange);
    });
    return registration;
  })();

  return withTimeout(readyPromise, timeoutMs, "Service worker activation");
}

function resolveApiBaseUrl() {
  const fromEnv = process.env.REACT_APP_API_URL?.replace(/\/$/, "");
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined") {
    const { hostname, protocol } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return `${protocol}//${hostname}:5000`;
    }
  }
  return window.location?.origin || "http://localhost:5000";
}

function postConfigToServiceWorker(registration, config) {
  return new Promise((resolve) => {
    const worker = registration.active || registration.waiting || registration.installing;
    if (!worker) {
      resolve(false);
      return;
    }
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      resolve(Boolean(event.data?.ok));
    };
    worker.postMessage(
      { type: "INIT_FIREBASE", config, apiBaseUrl: resolveApiBaseUrl() },
      [channel.port2]
    );
    window.setTimeout(() => resolve(false), 4000);
  });
}

function dispatchPlatformPush(payload) {
  const detail = payload || {};
  window.dispatchEvent(new CustomEvent(PLATFORM_PUSH_EVENT, { detail }));
  window.dispatchEvent(new CustomEvent(BOOKING_PUSH_EVENT, { detail }));
}

export function onPlatformPush(handler) {
  if (typeof handler !== "function") return () => {};
  const listener = (event) => handler(event.detail || {});
  window.addEventListener(PLATFORM_PUSH_EVENT, listener);
  return () => window.removeEventListener(PLATFORM_PUSH_EVENT, listener);
}

export function onBookingPush(handler) {
  return onPlatformPush(handler);
}

/** @deprecated use onPlatformPush */
export function onCaregiverBookingPush(handler) {
  return onPlatformPush(handler);
}

function canRegisterPush() {
  const role = api.getAuthRoleFromToken?.();
  return ["CUSTOMER", "CAREGIVER", "CARE_MANAGER", "ADMIN", "OPS"].includes(role);
}

function isSecurePushContext() {
  if (typeof window === "undefined") return false;
  const { hostname, protocol } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  return protocol === "https:";
}

function friendlyPushError(reason, code) {
  const codeMsg = String(code || "").toLowerCase();
  const msg = String(reason || "").toLowerCase();
  if (!isSecurePushContext()) {
    return "Browser push requires HTTPS on this domain (localhost is OK). Open the app via https://… or use in-app notifications from the bell.";
  }
  if (codeMsg === "20" || msg.includes("aborterror") || msg.includes("push service error")) {
    const chromeNote = isChromeBrowser()
      ? " In Chrome: chrome://settings/content/notifications → allow localhost, then DevTools → Application → Clear storage → reload."
      : "";
    const edgeNote = isEdgeBrowser()
      ? " In Edge: edge://settings/content/notifications → allow this site, then edge://serviceworker-internals unregister any stale workers for localhost."
      : "";
    return (
      "Browser could not subscribe to push (error 20). Clear site data (DevTools → Application), reload, and verify FIREBASE_WEB_VAPID_KEY." +
      chromeNote +
      edgeNote
    );
  }
  if (msg.includes("timed out")) {
    const edgeNote = isEdgeBrowser()
      ? " Edge sometimes hangs on push — try InPrivate window or Chrome, and allow notifications in edge://settings/content/notifications."
      : "";
    return `${reason || "Push registration timed out"}.${edgeNote}`;
  }
  if (codeMsg.includes("messaging/permission-blocked") || msg.includes("permission-blocked")) {
    return "Notifications are blocked in the browser. Open site settings for this page, allow notifications, then try again.";
  }
  if (codeMsg.includes("invalid-vapid-key") || msg.includes("invalid-vapid-key")) {
    return (
      "Invalid FIREBASE_WEB_VAPID_KEY — in Firebase Console open Project settings → Cloud Messaging → Web Push certificates, copy the public key pair, and set FIREBASE_WEB_VAPID_KEY in nemacareBE/.env"
    );
  }
  if (codeMsg.includes("token-subscribe-failed") || msg.includes("token-subscribe-failed")) {
    if (msg.includes("permission_denied") || msg.includes("api_key_service_blocked") || msg.includes("fcmregistrations")) {
      return (
        "Google Cloud blocked FCM registration (403). In console.cloud.google.com → APIs & Services → Enabled APIs, enable Firebase Installations API and FCM Registration API. Then APIs & Services → Credentials → your Web API key → API restrictions: allow those APIs (or use unrestricted for dev). Restart nemacareBE and hard-refresh the app."
      );
    }
    return (
      "FCM could not subscribe this browser — confirm FIREBASE_WEB_MESSAGING_SENDER_ID and FIREBASE_WEB_VAPID_KEY are from the same Firebase web app, add localhost under Authorized domains, then restart nemacareBE."
    );
  }
  if (msg.includes("permission_denied") && msg.includes("fcmregistrations")) {
    return (
      "FCM registration API returned 403 — enable Firebase Installations API + FCM Registration API in Google Cloud Console and allow them on FIREBASE_WEB_API_KEY (Credentials). Do not open the fcmregistrations URL in the browser; it is SDK-only."
    );
  }
  if (codeMsg.includes("installations") || msg.includes("installations")) {
    return (
      "Firebase Installations failed — check FIREBASE_WEB_API_KEY and FIREBASE_WEB_APP_ID match your Firebase web app, add localhost under Authorized domains, and clear site data (DevTools → Application → Clear storage)."
    );
  }
  if (msg.includes("push service error") || msg.includes("messaging/") || codeMsg.includes("messaging/")) {
    const detail = code ? `${code}` : reason;
    return `Firebase push registration failed (${detail}) — verify FIREBASE_WEB_* keys, VAPID key, and localhost in Firebase Authorized domains.`;
  }
  if (msg.includes("api key not valid") || msg.includes("invalid api key")) {
    return "Invalid FIREBASE_WEB_API_KEY on the server — copy keys from the same Firebase web app.";
  }
  return String(reason || code || "unknown error");
}

export const CHROME_OS_NOTIFICATION_HINT =
  "Windows is blocking Chrome alerts. Settings → System → Notifications → Google Chrome → On. Also chrome://settings/content/notifications → allow localhost.";

let osBlockWarned = false;

function warnOsBlockedOnce() {
  if (osBlockWarned || typeof window === "undefined") return;
  osBlockWarned = true;
  window.dispatchEvent(
    new CustomEvent("nema:os-notification-blocked", { detail: { hint: CHROME_OS_NOTIFICATION_HINT } })
  );
}

function buildOsNotificationOptions(displayBody, payload, tag) {
  const iconUrl = new URL("/favicon.ico", window.location.origin).href;
  return {
    body: displayBody,
    icon: iconUrl,
    badge: iconUrl,
    tag,
    renotify: true,
    requireInteraction: false,
    silent: false,
    data: payload,
  };
}

async function confirmSwNotification(registration, tag) {
  await new Promise((r) => window.setTimeout(r, 400));
  try {
    const list = await registration.getNotifications({ tag });
    return list.length > 0;
  } catch {
    return false;
  }
}

function showWindowNotification(title, options) {
  return new Promise((resolve) => {
    try {
      const n = new Notification(title, options);
      let settled = false;
      const finish = (ok) => {
        if (settled) return;
        settled = true;
        resolve(ok);
      };
      n.onshow = () => finish(true);
      n.onerror = () => finish(false);
      window.setTimeout(() => finish(false), 3000);
    } catch (err) {
      console.warn("[push] window.Notification failed:", err?.message || err);
      resolve(false);
    }
  });
}

/** Show a notification without FCM — tests Windows/Chrome display only. */
export async function showLocalTestNotification(title, body) {
  return showForegroundNotification(title, body, { type: "LOCAL_TEST" });
}

export async function showForegroundNotification(title, body, data = {}) {
  if (Notification.permission !== "granted") {
    console.warn("[push] OS notification skipped — permission not granted");
    return false;
  }
  const displayTitle = String(title || "Nema Care").slice(0, 160);
  const displayBody = String(body || "You have a new update.").slice(0, 500);
  const payload = data && typeof data === "object" ? data : {};
  const tag = `nema-${payload.type || "push"}-${Date.now()}`;
  const options = buildOsNotificationOptions(displayBody, payload, tag);

  let registration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch (err) {
    console.warn("[push] service worker not ready:", err?.message || err);
  }

  if (isChromeBrowser()) {
    const windowOk = await showWindowNotification(displayTitle, options);
    if (windowOk) {
      console.info("[push] OS notification shown via window.Notification (Chrome)");
      return true;
    }
  }

  if (registration) {
    try {
      await registration.showNotification(displayTitle, options);
      if (await confirmSwNotification(registration, tag)) {
        console.info("[push] OS notification confirmed via service worker");
        return true;
      }
      console.warn("[push] showNotification resolved but nothing appeared in the tray");
    } catch (err) {
      console.warn("[push] registration.showNotification failed:", err?.message || err);
    }

    const worker = registration.active || registration.waiting;
    if (worker) {
      try {
        const ack = await new Promise((resolve) => {
          const channel = new MessageChannel();
          channel.port1.onmessage = (event) => resolve(Boolean(event.data?.ok));
          worker.postMessage(
            { type: "SHOW_NOTIFICATION", title: displayTitle, body: displayBody, data: payload },
            [channel.port2]
          );
          window.setTimeout(() => resolve(false), 3000);
        });
        if (ack && (await confirmSwNotification(registration, tag))) {
          console.info("[push] OS notification confirmed via service worker message");
          return true;
        }
      } catch (err) {
        console.warn("[push] service worker message notification failed:", err?.message || err);
      }
    }
  }

  if (!isChromeBrowser()) {
    const windowOk = await showWindowNotification(displayTitle, options);
    if (windowOk) {
      console.info("[push] OS notification shown via window.Notification");
      return true;
    }
  }

  if (!navigator.serviceWorker?.controller) {
    console.warn("[push] reload once — service worker is not controlling this tab yet");
  }
  console.error("[push] OS notification not displayed —", CHROME_OS_NOTIFICATION_HINT);
  warnOsBlockedOnce();
  return false;
}

export async function registerPushNotifications({ force = false, refreshToken = false } = {}) {
  if (registrationInFlight && force) {
    console.warn("[push] cancelling in-flight registration — starting fresh");
    registrationInFlight = null;
    registrationStarted = false;
  }
  if (registrationInFlight) {
    return registrationInFlight;
  }

  registrationInFlight = (async () => {
    try {
      return await registerPushNotificationsInner({ force, refreshToken });
    } finally {
      registrationInFlight = null;
    }
  })();

  return registrationInFlight;
}

async function registerPushNotificationsInner({ force = false, refreshToken = false } = {}) {
  if (registrationStarted && !force && !refreshToken) {
    if (lastFcmToken) {
      return { ok: true, reason: "registered" };
    }
    registrationStarted = false;
  }
  if (typeof window === "undefined" || !("Notification" in window)) {
    lastRegistrationReason = "unsupported";
    return { ok: false, reason: "unsupported" };
  }
  if (!canRegisterPush()) {
    lastRegistrationReason = "role";
    return { ok: false, reason: "role" };
  }
  if (!api.getToken()) {
    lastRegistrationReason = "auth";
    return { ok: false, reason: "auth" };
  }

  registrationStarted = true;
  let serverDeliveryHint = "";

  async function runRegistration(allowRetry) {
    registrationStarted = true;
    console.info("[push] fetching server config…");
    const res = await api.getPushConfig();
    const enabled = Boolean(res?.enabled);
    const config = res?.config;
    const canDeliver = res?.canDeliver !== false;
    serverDeliveryHint = res?.deliveryHint || "";
    const diagnostics = res?.diagnostics || {};
    if (!enabled || !config?.apiKey || !config?.vapidKey) {
      registrationStarted = false;
      lastRegistrationReason = "not_configured";
      return { ok: false, reason: "not_configured" };
    }
    if (!canDeliver) {
      if (!serverDeliveryWarned) {
        serverDeliveryWarned = true;
        console.warn("[push] Server cannot deliver notifications yet:", serverDeliveryHint || diagnostics);
      }
    }
    if (diagnostics.vapidKeyLooksValid === false) {
      registrationStarted = false;
      lastRegistrationReason =
        "FIREBASE_WEB_VAPID_KEY looks invalid — use the Web Push public key from Firebase Console → Cloud Messaging.";
      return { ok: false, reason: lastRegistrationReason, serverDeliveryHint };
    }

    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") {
      registrationStarted = false;
      lastRegistrationReason = "denied";
      return { ok: false, reason: "denied" };
    }

    if (!isSecurePushContext()) {
      registrationStarted = false;
      lastRegistrationReason = "https_required";
      return { ok: false, reason: friendlyPushError("push service error"), serverDeliveryHint };
    }

    console.info("[push] registering service worker…");
    const swRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
      scope: "/",
      updateViaCache: "none",
    });
    try {
      await swRegistration.update();
    } catch {
      // ignore update errors in dev
    }
    await waitForServiceWorkerReady(swRegistration);

    const swConfig = {
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
    };
    let swReady = false;
    for (let attempt = 0; attempt < 3 && !swReady; attempt += 1) {
      swReady = await postConfigToServiceWorker(swRegistration, swConfig);
      if (!swReady) {
        await new Promise((r) => window.setTimeout(r, 400));
      }
    }
    if (!swReady) {
      console.warn(
        "[push] service worker Firebase init did not confirm — ensure public/firebase-config.js exists (npm prestart syncs from nemacareBE/.env)"
      );
    }

    const { initializeApp, getApps, deleteApp } = await import("firebase/app");
    const { getMessaging, getToken, onMessage, isSupported } = await import("firebase/messaging");

    const supported = await isSupported();
    if (!supported) {
      registrationStarted = false;
      return { ok: false, reason: "messaging_unsupported", serverDeliveryHint };
    }

    const MESSAGING_APP = "nema-fcm";
    let app = getApps().find((a) => a.name === MESSAGING_APP);
    if (!app) {
      app = initializeApp(swConfig, MESSAGING_APP);
    } else {
      const opts = app.options || {};
      if (opts.apiKey !== swConfig.apiKey || opts.projectId !== swConfig.projectId) {
        await deleteApp(app);
        app = initializeApp(swConfig, MESSAGING_APP);
      }
    }
    const messaging = getMessaging(app);

    let fcmToken;
    try {
      console.info(`[push] requesting FCM token (${detectWebPushPlatform()})…`);
      fcmToken = await withTimeout(
        getToken(messaging, {
          vapidKey: config.vapidKey,
          serviceWorkerRegistration: swRegistration,
        }),
        20000,
        "FCM getToken"
      );
    } catch (tokenErr) {
      if (allowRetry && isPushServiceError(tokenErr)) {
        console.warn("[push] push service error — clearing client state and retrying once");
        await clearMessagingClientState();
        return runRegistration(false);
      }
      throw tokenErr;
    }

    if (fcmToken) {
      persistFcmToken(fcmToken);
      const reg = await api.registerDeviceToken({
        token: fcmToken,
        platform: detectWebPushPlatform(),
      });
      lastRegistrationReason = "registered";
      console.info("[push] FCM token registered for this browser session", {
        platform: detectWebPushPlatform(),
        catchUp: Boolean(reg?.catchUpDelivered),
      });
      if (isChromeBrowser()) {
        window.setTimeout(async () => {
          const shown = await showForegroundNotification(
            "Nema Care",
            "Chrome push is on — look for alerts via Google Chrome in Windows notifications.",
            { type: "PUSH_TEST" }
          );
          if (!shown) {
            console.warn("[push] Chrome registered but OS notification was not shown");
          }
        }, 400);
      }
    } else {
      registrationStarted = false;
      lastRegistrationReason = "no_token";
      console.warn("[push] No FCM token — check notification permission and site settings");
    }

    if (!foregroundMessageListenerBound) {
      onMessage(messaging, (payload) => {
        console.info("[push] message received in app", payload?.data?.type || payload?.notification?.title);
        const data = payload?.data || {};
        dispatchPlatformPush({
          ...data,
          title: payload?.notification?.title || data.title,
          body: payload?.notification?.body || data.body,
        });
        const title = payload?.notification?.title || data.title || "Nema Care";
        const body = payload?.notification?.body || data.body || "";
        showForegroundNotification(title, body, data);
      });
      foregroundMessageListenerBound = true;
    }

    return {
      ok: Boolean(fcmToken),
      reason: fcmToken ? "registered" : "no_token",
      serverDeliveryHint: canDeliver ? "" : serverDeliveryHint,
    };
  }

  try {
    if (force) {
      registrationStarted = false;
    }
    return await withTimeout(runRegistration(true), 30000, "Push registration");
  } catch (err) {
    const raw = err?.message || String(err || "error");
    const code = err?.code != null ? String(err.code) : "";
    console.warn("[push] registration failed:", code || raw, err);
    registrationStarted = false;
    const friendly = friendlyPushError(raw, code);
    lastRegistrationReason = friendly;
    return { ok: false, reason: friendly, code: code || undefined, serverDeliveryHint };
  }
}

/** Refresh FCM token with Firebase and sync to server before test push. */
export async function refreshFcmTokenForTest() {
  if (typeof window === "undefined" || Notification.permission !== "granted") {
    return null;
  }
  if (!api.getToken() || !canRegisterPush()) {
    return null;
  }
  registrationStarted = false;
  registrationInFlight = null;
  const result = await registerPushNotifications({ refreshToken: true });
  if (result?.ok) {
    return getLastFcmToken();
  }
  registrationStarted = false;
  registrationInFlight = null;
  const retry = await registerPushNotifications({ force: true });
  return retry?.ok ? getLastFcmToken() : null;
}

/** Retry when the tab returns and permission was granted but no token is stored yet. */
export async function retryPushRegistrationIfNeeded() {
  if (typeof window === "undefined" || !("Notification" in window)) return null;
  if (Notification.permission !== "granted") return null;
  if (!api.getToken() || !canRegisterPush()) return null;
  if (lastFcmToken) return { ok: true, reason: "registered" };
  const needsForce =
    Boolean(lastRegistrationReason) &&
    lastRegistrationReason !== "registered" &&
    lastRegistrationReason !== "role" &&
    lastRegistrationReason !== "auth";
  return registerPushNotifications({ force: needsForce });
}

/** @deprecated use registerPushNotifications */
export async function registerCaregiverPushNotifications() {
  return registerPushNotifications();
}
